import trapFocus from '../../utils/trapFocus';
import { debounce } from 'throttle-debounce';
import { SETTINGS, BREAKPOINTS } from '../../utils/constants';
import reinitOkendoWidgets from '../../utils/reinitOkendoWidgets';

const initCollectionSearchFilters = () => {
  const filtersContainer = document.querySelector('[data-filters-container]');
  if (!filtersContainer) return;

  const filterPanel = document.querySelector('[data-filter-panel]');
  const filterBar = document.querySelector('[data-filter-bar]');
  const productGridEl = document.querySelector('[data-product-grid]');
  const openDrawerBtn = document.querySelector('[data-open-filter-panel]');
  const onOfferFilterBtn = document.querySelector('[data-on-offer-filter]');
  const filterFormEl = filtersContainer.querySelector('[data-filter-form]');
  const filterDrawerSubmitBtn = filterPanel.querySelector('[data-filters-submit]');
  const closeDrawerBtn = filterPanel.querySelector('[data-close-filter-drawer-button]');

  const loadMoreEl = document.querySelector('[data-load-more]');
  const resultsCount = filterBar.querySelector('[data-filters-results-count]');
  const isDesktop = () => window.innerWidth >= BREAKPOINTS.DESKTOP;
  const transitionNoneClass = 'transition-none';
  let isFilterBarConstrained = false;

  const sectionId = filterPanel.dataset.sectionId;

  const fetchResults = async (url) => {
    try {
      const response = await fetch(url);
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (err) {
      console.error('Error fetching filter results:', err);
    }
  };

  const scrollGridBackToTop = () => {
    const offset = document.querySelector('.collection-grid').offsetTop + document.querySelector('header').offsetHeight;
    window.scrollTo({ top: offset, behavior: 'smooth' });
  };

  const renderProductGrid = (resultsDoc) => {
    if (!resultsDoc) return;

    const newProductGrid = resultsDoc.querySelector('[data-product-grid]');
    if (newProductGrid) {
      productGridEl.innerHTML = newProductGrid.innerHTML;
      reinitOkendoWidgets();
    }

    const newLoadMore = resultsDoc.querySelector('[data-load-more]');
    if (loadMoreEl && newLoadMore) {
      loadMoreEl.innerHTML = newLoadMore.innerHTML;
      loadMoreEl.classList.remove('hidden');
    } else if (loadMoreEl && !newLoadMore) {
      loadMoreEl.classList.add('hidden');
    }

    const newResultsCount = resultsDoc.querySelector('[data-filters-results-count]');
    if (resultsCount && newResultsCount) resultsCount.innerHTML = newResultsCount.innerHTML;

    if (filterPanel.classList.contains('open')) scrollGridBackToTop();
  };

  const convertFormDataToUrl = () => {
    const formData = new FormData(filterFormEl);
    const params = new URLSearchParams(formData).toString();
    const url = new URL(window.location.href);
    url.search = params;
    return url;
  };

  const renderFilters = (resultsDoc) => {
    if (!resultsDoc) return;

    const newFiltersSubmitBtn = resultsDoc.querySelector('[data-filters-submit]');
    if (newFiltersSubmitBtn) {
      filterDrawerSubmitBtn.innerHTML = newFiltersSubmitBtn.innerHTML;
    }
  };

  const renderPage = async (url) => {
    try {
      const fetchUrl = sectionId ? `${url}&section_id=${sectionId}` : url;
      const resultsDoc = await fetchResults(fetchUrl);
      renderProductGrid(resultsDoc);
      renderFilters(resultsDoc);
    }
    catch (err) {
      console.error('Error rendering page:', err);
    }
  };

  const constrainFilterBarToPanel = () => {
    if (!filterPanel.classList.contains('open') || !isDesktop()) {
      filterBar.style.transform = '';
      return;
    }

    const topOffset = parseFloat(window.getComputedStyle(filterBar).top) || 0;
    const filterBarBottom = topOffset + filterBar.offsetHeight;
    const filterPanelTop = filterPanel.getBoundingClientRect().top;
    const overlap = filterBarBottom - filterPanelTop;

    filterBar.style.transform = overlap > 0 ? `translateY(-${overlap}px)` : '';
  };

  const addConstrainedFilterBarListeners = () => {
    if (isFilterBarConstrained) return;
    isFilterBarConstrained = true;
    filterBar.classList.add(transitionNoneClass);
    window.addEventListener('scroll', constrainFilterBarToPanel, { passive: true });
    window.addEventListener('resize', constrainFilterBarToPanel);
    constrainFilterBarToPanel();
  };

  const resetConstrainedFilterBar = () => {
    if (!isFilterBarConstrained) {
      filterBar.classList.remove(transitionNoneClass);
      filterBar.style.transform = '';
      return;
    }

    isFilterBarConstrained = false;
    filterBar.classList.remove(transitionNoneClass);
    window.removeEventListener('scroll', constrainFilterBarToPanel);
    window.removeEventListener('resize', constrainFilterBarToPanel);
    filterBar.style.transform = '';
  };

  const closeFilterDrawer = () => {
    filterPanel.classList.toggle('open');
    filterBar.classList.toggle('open');
    openDrawerBtn.classList.toggle('open');
    productGridEl.toggleAttribute('open');
    resetConstrainedFilterBar();
  };

  const handleCloseFilterDrawerClick = () => {
    closeFilterDrawer();
    closeDrawerBtn.removeEventListener('click', handleCloseFilterDrawerClick);
    filterDrawerSubmitBtn.removeEventListener('click', handleCloseFilterDrawerClick);
  };

  const handleFormInput = async () => {
    const url = convertFormDataToUrl();
    if (!url) return;

    //Update On Offer button
    const onOfferInput = filterFormEl.querySelector('[name="filter.p.m.custom.on_offer"]');
    if (onOfferInput && onOfferFilterBtn) {
      onOfferFilterBtn.classList.toggle('open', onOfferInput.checked);
    }

    window.history.pushState({}, '', url);
    renderPage(url.href);
  };


  const debouncedFormInput = debounce(SETTINGS.FILTER_UPDATE_DELAY, handleFormInput);

  const openFilterDrawer = () => {
    filterPanel.classList.toggle('open');
    filterBar.classList.toggle('open');
    openDrawerBtn.classList.toggle('open');
    productGridEl.toggleAttribute('open');
    closeDrawerBtn.addEventListener('click', handleCloseFilterDrawerClick);
    filterDrawerSubmitBtn.addEventListener('click', handleCloseFilterDrawerClick);
    filterFormEl.addEventListener('input', debouncedFormInput);

    // Set filters panel offset height
    const filtersOffsetHeight = filterBar.offsetHeight + document.querySelector('header').offsetHeight;
    filterPanel.style.setProperty('--filters-offset', `${filtersOffsetHeight}px`);

    if (filterPanel.classList.contains('open')) {
      addConstrainedFilterBarListeners();
    } else {
      resetConstrainedFilterBar();
    }
  };

  openDrawerBtn.addEventListener('click', () => openFilterDrawer());

  const onOfferFilter = () => {
    onOfferFilterBtn.classList.toggle('open');
    filterFormEl.addEventListener('input', debouncedFormInput);
    filterFormEl.querySelector('[name="filter.p.m.custom.on_offer"]').click();
  };

  onOfferFilterBtn?.addEventListener('click', () => onOfferFilter());

  const quickFilterButtons = document.querySelectorAll('[data-quick-filter]');

  const findQuickFilterCheckbox = (button) =>
    filterFormEl.querySelector(
      `input[type="checkbox"][name="${button.dataset.filterName}"][value="${CSS.escape(button.dataset.filterValue)}"]`
    );

  quickFilterButtons.forEach((button) => {
    const checkbox = findQuickFilterCheckbox(button);

    if (!checkbox) {
      button.disabled = true;
      button.classList.add('opacity-40', 'pointer-events-none');
      return;
    }

    checkbox.addEventListener('change', () => button.classList.toggle('active', checkbox.checked));

    button.addEventListener('click', () => {
      filterFormEl.addEventListener('input', debouncedFormInput);
      checkbox.click();
    });
  });

};

export default initCollectionSearchFilters;
